import {DeleteOutlined, EditOutlined, WarningOutlined} from '@ant-design/icons';
import {Divider} from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import InfoBox from '@/components/InfoBox';
import {Tooltip} from '@/components/Tooltip';
import PartnerProposal
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal';
import ContributionGoods
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/ContributionGoods';
import ContributionInput
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/ContributionInput';
import VerifyListItem
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/VerifyListItem';
import style from '@/modules/proposalRequest/components/ProposalPrivatePartners/index.less';
import {renderMunicipality, renderProvince} from '@/utils/location';
import {showPrice} from '@/utils/utils';

const PartnerStateProposal = ({
  data,
  extraTitle,
  onEditPartner,
  onDeletePartner,
  onlyRead,
  onDeleteRepresentative,
  onEditRepresentative,
  onAddRepresentative,
  addLabelText,
  onChange,
  index,
  disableContribution,
  proposal,
  cashEdit = true,
  isLast,
  ...props
}) => {
  const {t} = useTranslation('proposalRequest');
  const {
    representative,
    address,
    entityDenomination,
    reeup,
    integrationSubordination,
    contribution,
    goods
  } = data || {};
  
  const onChangeContribution = useCallback(({value, isMatrimonialCommunity}) => {
    data.contribution = value;
    data.isMatrimonialCommunity = isMatrimonialCommunity;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const onChangeGoods = useCallback((goods) => {
    data.goods = goods;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const mainAddress = useMemo(() => `${address?.abstract} / ${renderProvince(address?.province)} / ${renderMunicipality(address?.municipality)}`, [address?.abstract, address?.municipality, address?.province]);
  
  return (
    <div>
      <div className="mb-3 flex items-center">
        <div className="text-lg font-bold">{entityDenomination}</div>
        <div className="ml-2 flex-1 flex items-center justify-between">
          <div/>
          {!onlyRead && <div className="flex items-center justify-between">
            <div className={classNames(style.actionButtonContainer, 'cursor-pointer')} onClick={onEditPartner}>
              <EditOutlined/>
            </div>
            <div className={classNames(style.actionButtonContainer, 'cursor-pointer ml-4')}
              onClick={onDeletePartner}>
              <DeleteOutlined/>
            </div>
          </div>}
        </div>
        {!!extraTitle && <div className="ml-2 flex-1">{extraTitle}</div>}
      </div>
      <VerifyListItem
        labelKey={t('proposalRequest.form.partners.modal.reeup')} value={reeup}/>
      <VerifyListItem
        labelKey={t('proposalRequest.form.partners.modal.integrationSubordination')}
        value={integrationSubordination}/>
      <VerifyListItem labelKey={t('address')} truncate>
        <Tooltip title={mainAddress} className="truncate">{mainAddress}</Tooltip>
      </VerifyListItem>
      {onlyRead || !cashEdit ? <VerifyListItem labelKey={t('proposalRequest.form.partners.contribution')}
        value={showPrice(contribution || '0')}/> :
        <VerifyListItem labelKey={t('proposalRequest.form.partners.contribution')} fullSpace>
          <ContributionInput
            {...props}
            value={{value: contribution || 0}}
            onChange={onChangeContribution}
            hideCommunityProperty/>
        </VerifyListItem>}
      {!disableContribution &&
        <VerifyListItem labelKey={t('proposalRequest.form.partners.contributionBienes.label')} fullSpace>
          <ContributionGoods value={goods} onChange={onChangeGoods} index={index} onlyRead={onlyRead}
            hideCommunityProperty proposal={proposal}/>
        </VerifyListItem>}
      {representative ? <div className="mt-4">
        <PartnerProposal
          data={{...representative, representative: true}}
          disableContribution
          onlyRead={onlyRead}
          onDeletePartner={onDeleteRepresentative}
          onEditPartner={onEditRepresentative}
          isStatePartnerForm={true}
          isLast={true}
        />
      </div> : !cashEdit ? null :
        onAddRepresentative && <div onClick={onAddRepresentative} className={'cursor-pointer text-primary my-4'}>
          {t(addLabelText || 'proposalRequest.form.partners.addText')}
          {!representative && <InfoBox
            icon={<WarningOutlined/>}
            hideIcon={false}
            className="text-error not-italic text-base"
            title={'proposalRequest.form.partners.representativePartners.required'}
            translate={'proposalRequest'}/>}
        </div>}
      {onlyRead && !isLast && <Divider/>}
    </div>
  );
};

PartnerStateProposal.propTypes = {
  data: PropTypes.object,
  addLabelText: PropTypes.string,
  onlyRead: PropTypes.bool,
  isLast: PropTypes.bool,
  extraTitle: PropTypes.any,
  onEditPartner: PropTypes.func,
  onAddRepresentative: PropTypes.func,
  onEditRepresentative: PropTypes.func,
  onDeleteRepresentative: PropTypes.func,
  onDeletePartner: PropTypes.func,
  onChange: PropTypes.func,
  proposal: PropTypes.object,
  disableContribution: PropTypes.bool,
  index: PropTypes.number,
  cashEdit: PropTypes.bool,
};

PartnerStateProposal.defaultProps = {};

export default memo(PartnerStateProposal);
